import { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, Search as SearchIcon } from 'lucide-react';
import {
  useTransactionBuilderStore,
  TransactionBuilderPageType,
} from '../../components/transaction-builder/useTransactionBuilderStore';
import { TransactionBuilderHeader } from '../../components/transaction-builder/TransactionBuilderHeader';
import { TransactionBuilderFooter } from '../../components/transaction-builder/TransactionBuilderFooter';
import { TransactionBuilderPolicyRow } from '../../components/transaction-builder/TransactionBuilderPolicyRow';
import { PolicyDetailSheet } from '@/pages/policies/components/policy-detail-sheet';
import { useTransactionBuilderPolicies } from '@/hooks/policy/use-transaction-builder-policies';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useListAddressBookEntries } from '@/hooks/organization/use-list-address-book';
import { useAuthClient } from '@/lib/authClient';
import type {
  Account,
  NetworkType,
  Policy,
  PolicyWithRateLimitInfo,
} from '@mls-wallet/shared-types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { canUserInitiatePolicy } from '@/components/transaction-builder/transaction-policy-validation';
import { formatLocalizedDateTimeWithShortTz } from '@/lib/formatLocalizedDateTimeWithShortTz';

type PolicyFilter = 'all' | 'transfer' | 'contract';

/**
 * Tab button component for policy type filtering
 */
function PolicyFilterTab({
  children,
  isActive,
  onClick,
}: {
  children: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      className={`px-[12px] py-[6px] rounded-[31px] font-medium text-[14px] leading-[20px] transition-colors ${
        isActive
          ? 'bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white'
          : 'bg-transparent text-zinc-700 dark:text-zinc-400 hover:bg-white/60 hover:text-zinc-900 active:bg-white/80 dark:hover:bg-zinc-950/40 dark:hover:text-zinc-200 dark:active:bg-zinc-950/60'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Policy selection page for the transaction builder
 * Displays available policies for the transaction with search and filtering
 */
export function TransactionBuilderPolicySelectionPage({
  account,
  network,
  embedded = false,
  onCancel,
}: {
  account: Account;
  network: NetworkType;
  embedded?: boolean;
  onCancel?: () => void;
}): React.ReactElement {
  const setPolicy = useTransactionBuilderStore((state) => state.setPolicy);
  const navigateForwardToPage = useTransactionBuilderStore(
    (state) => state.navigateForwardToPage
  );

  const { organization } = useMyOrganization();
  const { user } = useAuthClient();
  const { policies, isLoading } = useTransactionBuilderPolicies({
    accountId: account.id,
    networkId: network,
  });
  const {
    entries: contractAddressBookEntries,
    hasMore: hasMoreContractAddressBookEntries,
    isLoadingMore: isLoadingMoreContractAddressBookEntries,
    loadMore: loadMoreContractAddressBookEntries,
  } = useListAddressBookEntries({
    enabled: Boolean(organization?.id),
    type: 'CONTRACT',
    pagination: { limit: 100 },
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [policyFilter, setPolicyFilter] = useState<PolicyFilter>('all');
  const [selectedPolicyForDetails, setSelectedPolicyForDetails] =
    useState<Policy | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [rateLimitBlockedPolicy, setRateLimitBlockedPolicy] =
    useState<PolicyWithRateLimitInfo | null>(null);

  // Reset policy when the page loads
  // This is to ensure that the header display is correct
  useEffect(() => {
    setPolicy(undefined);
  }, [setPolicy]);

  /**
   * Filter policies based on account, network, search query, and tab filter
   */
  const filteredPolicies = useMemo(() => {
    if (!policies) {
      return [];
    }

    const matchingPolicies = policies.filter(
      (policy: PolicyWithRateLimitInfo) => {
        // Filter 1: Search filter
        const query = searchQuery.trim().toLowerCase();
        if (query) {
          const nameMatch = policy.name.toLowerCase().includes(query);
          const descriptionMatch = policy.description
            ?.toLowerCase()
            .includes(query);
          if (!nameMatch && !descriptionMatch) return false;
        }

        // Filter 2: Tab filter (transaction type)
        if (policyFilter === 'transfer') {
          return policy.transactionType === 'TOKEN_TRANSFER';
        } else if (policyFilter === 'contract') {
          return policy.transactionType === 'CONTRACT_INTERACTION';
        }
        // 'all' shows everything (including ANY type)

        return true;
      }
    );

    if (!user) {
      return matchingPolicies;
    }

    return matchingPolicies
      .map((policy, index) => ({
        policy,
        index,
        isCurrentUserInitiator: canUserInitiatePolicy(policy, user.id),
      }))
      .sort(
        (a, b) =>
          Number(b.isCurrentUserInitiator) - Number(a.isCurrentUserInitiator) ||
          a.index - b.index
      )
      .map(({ policy }) => policy);
  }, [policies, searchQuery, policyFilter, user]);

  const visibleContractAddresses = useMemo(
    () =>
      filteredPolicies.flatMap((policy) =>
        policy.transactionType === 'CONTRACT_INTERACTION'
          ? (policy.contractInteractionCondition ?? []).map((condition) =>
              condition.contractAddress.toLowerCase()
            )
          : []
      ),
    [filteredPolicies]
  );

  const contractAddressNameByAddress = useMemo(
    () =>
      new Map(
        contractAddressBookEntries.map((entry) => [
          entry.address.toLowerCase(),
          entry.name,
        ])
      ),
    [contractAddressBookEntries]
  );

  useEffect(() => {
    if (
      visibleContractAddresses.length === 0 ||
      !hasMoreContractAddressBookEntries ||
      isLoadingMoreContractAddressBookEntries
    ) {
      return;
    }

    const hasMissingVisibleContractEntry = visibleContractAddresses.some(
      (address) => !contractAddressNameByAddress.has(address)
    );

    if (!hasMissingVisibleContractEntry) {
      return;
    }

    // Keep paging until every visible contract policy can render its saved
    // address-book label instead of falling back to the raw address.
    void loadMoreContractAddressBookEntries();
  }, [
    contractAddressNameByAddress,
    hasMoreContractAddressBookEntries,
    isLoadingMoreContractAddressBookEntries,
    loadMoreContractAddressBookEntries,
    visibleContractAddresses,
  ]);

  const handlePolicySelect = (policy: PolicyWithRateLimitInfo) => {
    if (!user || !canUserInitiatePolicy(policy, user.id)) {
      return;
    }

    if (policy.rateLimitInfo.rateLimitReached) {
      setRateLimitBlockedPolicy(policy);
      return;
    }

    setPolicy(policy);
    navigateForwardToPage({
      page: TransactionBuilderPageType.TRANSACTION_DETAILS_PAGE,
    });
  };

  const handleSeeDetails = (policy: Policy) => {
    setSelectedPolicyForDetails(policy);
    setIsDetailSheetOpen(true);
  };

  const rateLimitResetAtDisplay = rateLimitBlockedPolicy?.rateLimitInfo.resetAt
    ? formatLocalizedDateTimeWithShortTz(
        rateLimitBlockedPolicy.rateLimitInfo.resetAt
      )
    : null;

  return (
    <div className={embedded ? 'flex min-h-full flex-col' : undefined}>
      {!embedded && (
        <TransactionBuilderHeader
          title="Which policy do you want to use to create your transaction?"
          showBackButton={true}
        />
      )}
      <div
        className={`flex flex-col w-full max-w-[880px] mx-auto px-4 gap-[15px] ${embedded ? 'flex-1 pt-[80px] pb-8' : 'py-6'}`}
      >
        {!embedded && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() =>
                navigateForwardToPage({
                  page: TransactionBuilderPageType.JSON_IMPORT_PAGE,
                })
              }
            >
              Import transactions from JSON
            </Button>
          </div>
        )}
        {/* Filter row: Search bar + Tabs */}
        <div className="bg-neutral-50 dark:bg-neutral-900 border border-solid border-white dark:border-zinc-800 flex flex-col sm:flex-row gap-[15px] items-stretch overflow-clip p-[5px] rounded-3xl sm:rounded-full shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] my-[10px]">
          {/* Search bar */}
          <div className="bg-white dark:bg-zinc-950 border border-solid border-zinc-100 dark:border-zinc-800 rounded-full px-[22px] py-[10px] flex items-center gap-[10px] flex-1">
            <SearchIcon className="w-4 h-4 text-zinc-500 dark:text-zinc-400 shrink-0" />
            <input
              type="text"
              placeholder="Search policies"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[16px] text-foreground placeholder:text-zinc-500 dark:placeholder:text-zinc-400 outline-none border-none"
            />
          </div>

          {/* Tab filter */}
          <div className="bg-zinc-100 dark:bg-zinc-800 border border-solid border-white dark:border-zinc-700 flex items-start overflow-clip p-[5px] rounded-[36px] shrink-0">
            <PolicyFilterTab
              isActive={policyFilter === 'all'}
              onClick={() => setPolicyFilter('all')}
            >
              All policies
            </PolicyFilterTab>
            <PolicyFilterTab
              isActive={policyFilter === 'transfer'}
              onClick={() => setPolicyFilter('transfer')}
            >
              Transfer Policies
            </PolicyFilterTab>
            <PolicyFilterTab
              isActive={policyFilter === 'contract'}
              onClick={() => setPolicyFilter('contract')}
            >
              Contract Policies
            </PolicyFilterTab>
          </div>
        </div>

        {/* Policy list */}
        <div className="flex flex-col gap-[15px]">
          {isLoading && (
            <div className="text-center text-zinc-500 dark:text-zinc-400 py-8">
              Loading policies...
            </div>
          )}

          {!isLoading && filteredPolicies.length === 0 && (
            <div className="text-center text-zinc-500 dark:text-zinc-400 py-8">
              {searchQuery
                ? `No policies found matching "${searchQuery}"`
                : 'No policies available for this account and blockchain network'}
            </div>
          )}

          {!isLoading &&
            filteredPolicies.map((policy) => (
              <TransactionBuilderPolicyRow
                key={policy.id}
                policy={policy}
                contractAddressNameByAddress={contractAddressNameByAddress}
                isCurrentUserInitiator={
                  user ? canUserInitiatePolicy(policy, user.id) : false
                }
                onClick={() => handlePolicySelect(policy)}
                onSeeDetails={handleSeeDetails}
              />
            ))}
        </div>
      </div>
      <TransactionBuilderFooter
        embedded={embedded}
        showContinueButton={false}
        secondaryAction={
          embedded && onCancel
            ? { label: 'Cancel', onClick: onCancel }
            : undefined
        }
      />

      {/* Policy Detail Sheet */}
      <PolicyDetailSheet
        policy={selectedPolicyForDetails}
        isOpen={isDetailSheetOpen}
        onOpenChange={setIsDetailSheetOpen}
      />

      <Dialog
        open={rateLimitBlockedPolicy !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRateLimitBlockedPolicy(null);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-[425px]"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Rate limit reached
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Can't use this policy right now as its rate limit has been fully
              used for this time period.
              {rateLimitResetAtDisplay ? (
                <>
                  <br />
                  It refreshes on <strong>{rateLimitResetAtDisplay}</strong>.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRateLimitBlockedPolicy(null)}
              className="w-full font-normal sm:w-auto"
            >
              Choose another policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
